/**
 * The native app the page may run in (Revisa for iOS and iPadOS, and for Android): what it provides in place of the
 * page's own (its sidebar, its navigation), and the bridge to it. The app says so before the page runs
 * (`window.revisaShell`, injected at the document's start); the page answers with what it understands (`ready`), so
 * either can be newer than the other: the page draws its own of anything the app doesn't provide, and the app takes
 * over only what the page says it can hand over. Nothing here depends on the platform, only on what's said.
 */

/** One of what's being shared to Revisa (the share sheet's): its name, and what it is. */
export interface Sharing {
  label: string;
  kind: "page" | "image" | "pdf" | "file" | "text";
}

/** What the app says of itself. */
interface Shell {
  /** Which app ("ios", "android"…): its look (`data-shell`), and what can't be said as a capability (logs). */
  platform: string;
  /** Its bridge's version. */
  bridge: number;
  /** What it provides instead of the page: "sidebar", "navigation"… */
  provides: readonly string[];
  /** Kept warm, out of sight, for a page to be shown in later (until it is: `unpark`). */
  parked?: boolean;
  /** How much larger than usual you read text (the system's text size: 1, as it comes). */
  textScale?: number;
  /**
   * The part of the web app this page is, alone (the app drawing the rest of itself around it): "sidebar", "share".
   * None: a page, whole.
   */
  fragment?: string;
  /** What's being shared, when the page is where it goes (`share`): each one's name and kind. */
  sharing?: readonly Sharing[];
}

/** The page's text (and what's sized with it) `scale` times its usual size: the system's text size, in the app. */
export function scaleText(scale: number): void {
  document.documentElement.style.setProperty("--shell-text-scale", String(scale));
}

/** Whether the app keeps the page warm, out of sight (as a hidden tab: its live updates wait until it's shown). */
export const parked = (): boolean => shell()?.parked === true;

/** The page is shown now: what waits for that (as for a tab coming back into sight) goes on. */
export function unpark(): void {
  const app = shell();
  if (app?.parked) {
    app.parked = false;
    document.dispatchEvent(new Event("visibilitychange"));
  }
}

/** The way to the app (WebKit's message handler; the Android app's, the same): a message, answered (a promise). */
interface Handler {
  postMessage: (message: unknown) => Promise<unknown> | undefined;
}

/** This page's bridge version, and what it can hand over to an app that provides it. */
const BRIDGE = 1;
const SUPPORTS = [
  "sidebar",
  "navigation",
  "painted",
  "title",
  "menus",
  "lift",
  "confirm",
  "popovers",
  "peek",
  "offline",
  "session",
  "token",
  "back",
] as const;

/** The app the page runs in, if any (as it said before the page ran). */
export function shell(): Shell | undefined {
  return (globalThis as { revisaShell?: Shell }).revisaShell;
}

/** What a fragment hears as the app shows it again (its drawer opened, its column back): nativeApi's `revealed`. */
export const REVEALED = "revisa:revealed";

/** The part of the web app this page is, alone (`Shell.fragment`), if it's one. */
export const fragment = (): string | undefined => shell()?.fragment;

/** What's being shared, where the page is where it goes (`Shell.sharing`). */
export const sharing = (): readonly Sharing[] => shell()?.sharing ?? [];

/** Whether the app provides `what` (and the page then leaves it to it). */
export function provides(what: (typeof SUPPORTS)[number]): boolean {
  return shell()?.provides.includes(what) ?? false;
}

/** The app's message handler, if there's one: WebKit's, or the Android app's (`revisaBridge`, android/…/Web.kt). */
function handler(): Handler | undefined {
  const g = globalThis as { webkit?: { messageHandlers?: { revisa?: Handler } }; revisaBridge?: Handler };
  return g.webkit?.messageHandlers?.revisa ?? g.revisaBridge;
}

/** Tells the app something (`type`, with `body`); its answer, if it gives one (one it doesn't know: "unsupported"). */
export function post(type: string, body: Record<string, unknown> = {}): Promise<unknown> {
  const h = handler();
  if (!h) {
    return Promise.resolve(undefined);
  }
  return Promise.resolve(h.postMessage({ type, ...body }));
}

/**
 * Marks the page for what the app provides (`<html data-shell="ios" data-provides="sidebar navigation">`), so its
 * own of those stay out of the way, and answers the app with what it understands.
 */
export function greetShell(): void {
  const app = shell();
  if (!app) {
    return;
  }
  const root = document.documentElement;
  root.dataset["shell"] = app.platform;
  if (app.fragment) {
    root.dataset["fragment"] = app.fragment;
  }
  root.dataset["provides"] = app.provides.filter((p) => (SUPPORTS as readonly string[]).includes(p)).join(" ");
  scaleText(app.textScale ?? 1);
  post("ready", { bridge: BRIDGE, supports: SUPPORTS }).catch(() => undefined);
}

/** What in an address says where in a page you are, not how it's shown (`?folder=`: a folder of the items). */
const PLACES = ["folder"] as const;

/**
 * Going to another page (`url`, from `from`): an app that provides navigation opens it as a page of its own (its
 * stack, its transitions); true then, and the page stays where it is. The same page with other parameters (filters,
 * a tab) stays here.
 */
export function visitNatively(url: URL, from: URL): boolean {
  // The same page in another view (its tab, its filters) stays here; another place in it (a folder) is another page.
  // A fragment shows no page: anywhere it goes is the app's to show.
  const same =
    !fragment() &&
    url.pathname === from.pathname &&
    PLACES.every((k) => url.searchParams.get(k) === from.searchParams.get(k));
  if (!provides("navigation") || url.origin !== from.origin || same) {
    return false;
  }
  post("visit", { path: `${url.pathname}${url.search}${url.hash}` }).catch(() => undefined);
  return true;
}

/** Editors open whose drags are theirs (counted: the app's told they're no longer as the last closes). */
let holding = 0;

/**
 * The page's drags are an editor's (moving, resizing, selecting, scrolling inside it) until the function it returns is
 * called: an app's own gestures over the page (its back swipe, its sheet's drag) are off meanwhile. `box`: the
 * editor's own box, whose top (in the page) the app's told, again as the page's laid out otherwise: it keeps the page
 * from scrolling so far down that only the editor's left in view (its drags all the editor's, none to scroll back up).
 */
export function holdGestures(box?: () => HTMLElement | null): () => void {
  holding += 1;
  let top: number | null = null;
  const say = () => {
    const el = box?.();
    const now = el ? el.getBoundingClientRect().top + globalThis.scrollY : null;
    if (now !== top || holding === 1) {
      top = now;
      post("gestures", { held: true, top }).catch(() => undefined);
    }
  };
  say();
  const watch = box && typeof ResizeObserver !== "undefined" ? new ResizeObserver(say) : null;
  watch?.observe(document.body);
  let released = false;
  return () => {
    if (released) {
      return;
    }
    released = true;
    watch?.disconnect();
    holding -= 1;
    if (holding === 0) {
      post("gestures", { held: false }).catch(() => undefined);
    }
  };
}

/** How much of the page the app's keyboard covers (CSS px): room an editor leaves after its content, behind it. */
let keyboard = 0;

export const keyboardInset = (): number => keyboard;

/** Heard on the document as the app's keyboard comes, moves or goes (`keyboardInset` says how much it covers). */
export const KEYBOARD = "revisa:keyboard";

/** The app's keyboard covers `px` of the page now (0: it's down): `--keyboard`, for an editor's room under it. */
export function setKeyboard(px: number): void {
  keyboard = px;
  document.documentElement.style.setProperty("--keyboard", `${px}px`);
  document.dispatchEvent(new Event(KEYBOARD));
}

/** What's being typed into where the focus is (a field's value, an editable's text). */
function typed(): string {
  const at = document.activeElement;
  if (at instanceof HTMLInputElement || at instanceof HTMLTextAreaElement) {
    return at.value;
  }
  return at instanceof HTMLElement && at.isContentEditable ? (at.textContent ?? "") : "";
}

/**
 * The app's keyboard has a row of its own over it (a spreadsheet's: arrows, Tab and Shift as keys, `symbols` typed)
 * until the function it returns is called; and no corrections nor suggestions while what's typed is `plain` (a
 * formula: not words). An app that has none says so and nothing changes.
 */
export function keyboardAccessory(symbols: readonly string[], plain?: (text: string) => boolean): () => void {
  post("accessory", { kind: "sheet", symbols }).catch(() => undefined);
  let was = false;
  const check = () => {
    const now = plain?.(typed()) ?? false;
    if (now !== was) {
      was = now;
      post("plainTyping", { on: now }).catch(() => undefined);
    }
  };
  document.addEventListener("input", check, true);
  document.addEventListener("focusin", check, true);
  return () => {
    document.removeEventListener("input", check, true);
    document.removeEventListener("focusin", check, true);
    if (was) {
      post("plainTyping", { on: false }).catch(() => undefined);
    }
    post("accessory", { kind: null }).catch(() => undefined);
  };
}
